import { jsx } from 'react/jsx-runtime';
import { useOrganization as useOrganization$1, useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { createContext, useContext, useMemo } from 'react';
import { useApp } from './AppContext.js';

const OrganizationContext = createContext(undefined);
/**
 * Inner provider that uses Clerk hooks - only rendered when Clerk is available
 */
function OrganizationProviderWithClerk({ children }) {
    const { organization: clerkOrganization, isLoaded: clerkLoaded } = useOrganization$1();
    const { getToken } = useAuth();
    const { getApiUrl } = useApp();
    // Fetch database organization data when Clerk organization is selected
    const { data: dbOrgData, isLoading: dbLoading } = useQuery({
        queryKey: ['Organization', 'current', clerkOrganization?.id],
        queryFn: async () => {
            if (!clerkOrganization?.id) {
                return null;
            }
            try {
                const baseURL = getApiUrl();
                if (!baseURL) {
                    throw new Error('API base URL is not defined');
                }
                const token = await getToken();
                const headers = {
                    'Content-Type': 'application/json',
                };
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }
                const response = await axios.get(`${baseURL}/organizations/current`, { headers });
                return response.data.data;
            }
            catch (error) {
                console.error('Error fetching organization from database:', error);
                return null;
            }
        },
        enabled: !!clerkOrganization?.id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
    const value = useMemo(() => {
        const dbOrganization = dbOrgData || null;
        const organizationSelected = !!clerkOrganization;
        const isInternal = dbOrganization?.isInternal ?? false;
        const isLoading = !clerkLoaded || (organizationSelected && dbLoading);
        return {
            clerkOrganization,
            dbOrganization,
            organizationSelected,
            isInternal,
            isLoading,
            organization: clerkOrganization, // Maintain backward compatibility
        };
    }, [clerkOrganization, dbOrgData, clerkLoaded, dbLoading]);
    return jsx(OrganizationContext.Provider, { value: value, children: children });
}
/**
 * Fallback provider when Clerk is not available
 */
function OrganizationProviderWithoutClerk({ children }) {
    const value = useMemo(() => ({
        clerkOrganization: null,
        dbOrganization: null,
        organizationSelected: false,
        isInternal: false,
        isLoading: false,
        organization: null,
    }), []);
    return jsx(OrganizationContext.Provider, { value: value, children: children });
}
function OrganizationProvider({ children }) {
    const { config } = useApp();
    // If no Clerk publishable key, use the fallback provider
    // This prevents Clerk hooks from being called when ClerkProvider isn't available
    if (!config?.clerkPublishableKey) {
        return jsx(OrganizationProviderWithoutClerk, { children: children });
    }
    // When Clerk key is available, use the full provider with Clerk hooks
    return jsx(OrganizationProviderWithClerk, { children: children });
}
function useOrganization() {
    const context = useContext(OrganizationContext);
    if (context === undefined) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
}

export { OrganizationProvider, useOrganization };
//# sourceMappingURL=OrganizationContext.js.map
